import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryParam, StringParam } from 'use-query-params';
import { CookiesStorage } from '@ltvco/refresh-lib';
import { Grid, IconButton, Stack, Text } from '@ltvco/refresh-lib/theme';
import CloseIcon from '@mui/icons-material/Close';
import { DateUtil, useScreenSize } from '@ltvco/refresh-lib/utils';
import {
  closeSnackbar,
  EmailSettingsCard,
  EmailVerificationErrorModal,
  EmailVerificationSuccessModal,
  MonitoredReportsModal,
  NeedAssistanceSidebar,
  useAccount,
  useArticles,
  useSnackbar,
  useVideoArticles,
} from '@ltvco/refresh-lib/v1';
import { styled } from '@ltvco/refresh-lib/theme';
import { constants } from 'appConstants';
import { ArticlesContainer } from '../components/Articles/ArticlesContainer';
import { RelatedArticles } from 'components/Articles/RelatedArticles';
import { RelatedVideoArticles } from 'components/Articles/RelatedVideoArticles';
import { SidenavContainer } from '../components/sidenav/SidenavContainer';
import { SideBarMigrationCta } from '@ltvco/refresh-lib/v2';
import { useFirstRecordSearch } from '../hooks/useFirstRecordSearch';

const StyledContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  '&&': {
    '& > *': {
      width: '100%',
      maxWidth: '100%',
      border: `1px solid ${theme.palette.background.light}`,
      boxShadow: 'none',
      '& > button': {
        width: '95%',
      },
      '& > div button': {
        paddingRight: 0,
      },
    },
  },
}));

export function DashboardParent({
  contactUsHandler,
  handleLogout,
}: {
  contactUsHandler: () => void;
  handleLogout: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useScreenSize();

  const dateUtil = new DateUtil();
  const currentDate = new Date();

  // State
  const [monitoredReportsOpen, setMonitoredReportsOpen] = useState(false);
  const [showEmailExpiredModal, setShowEmailExpiredModal] = useState(false);
  const [showEmailSuccessModal, setShowEmailSuccessModal] = useState(false);

  // Users were seeing notifications every time the account info was updated
  // this is a fix that will only show the notification once
  const [seenSiteNotifications, setSeenSiteNotifications] = useState<string[]>(
    []
  );

  const { data: account } = useAccount(true);
  const [statusParam, setStatusParam] = useQueryParam('status', StringParam);
  const { data: videoArticles } = useVideoArticles();
  const { data: articles } = useArticles();

  function closeMonitoredReportsModal() {
    setMonitoredReportsOpen(false);
  }

  // Use hook to gate subscribe flow and continue first record search
  useFirstRecordSearch(account);

  // Removed inline first record search gating/continuation in favor of hook

  useEffect(() => {
    if (localStorage.getItem('pausedAccountSnackbarId')) {
      try {
        closeSnackbar(
          JSON.parse(localStorage.getItem('pausedAccountSnackbarId') || '')
        );
      } catch (e) {
        console.log('error', e);
      }
    }
    handleSiteNotifications();
    handleReactivationForPausedAccountsBanner();
  }, [account]);

  useEffect(() => {
    if (!account) return;

    const isEmailVerified = account?.account.user_info?.verified_email;
    const hasShownSuccessModal =
      account?.account.user_settings?.hide_email_verification_success;

    if (
      !isEmailVerified &&
      statusParam === 'success' &&
      !hasShownSuccessModal
    ) {
      setStatusParam(undefined, 'replaceIn');
      setShowEmailSuccessModal(true);
    } else if (!isEmailVerified && statusParam === 'expired') {
      setStatusParam(undefined, 'replaceIn');
      setShowEmailExpiredModal(true);
    } else if (!isEmailVerified) {
      //setShowEmailPromptModal(true);
    }
  }, [account, statusParam, setStatusParam]);

  function handleReactivationForPausedAccountsBanner() {
    if (!account) return;

    const isInPausedStatus = account?.meta?.subscription_state === 'paused';
    if (!isInPausedStatus) return;

    const today = currentDate.toISOString();
    const expirationDate = account?.account.subscription_info?.normalize_date;

    if (!expirationDate) return;

    const isAfterCurrentDate = dateUtil.isAfter(today, expirationDate);

    function handleReactivationClick() {
      closeSnackbar(pausedAccountSnackbarId);
      navigate('/reactivation');
    }

    const expiredMessage = (
      <Text>
        <b>
          Your account expired on{' '}
          {dateUtil.parseDateFromString(
            account?.account.subscription_info?.normalize_date,
            'M/d/y'
          )}{' '}
          . Reactivate to regain access.
        </b>
      </Text>
    );

    const cancelledMessage = (
      <Text>
        <b>
          Your account with member ID {account?.account?.user_info?.user_code}{' '}
          has been cancelled.
        </b>{' '}
        <br /> You have until{' '}
        {dateUtil.parseDateFromString(
          account?.account.subscription_info?.normalize_date,
          'M/d/y'
        )}{' '}
        to continue running reports. Please reactivate now to maintain access.
        Visit{' '}
        <span
          color={'primary.contrastText'}
          onClick={handleReactivationClick}
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
        >
          here
        </span>{' '}
        to reactivate.
      </Text>
    );

    const message = isAfterCurrentDate ? expiredMessage : cancelledMessage;

    const pausedAccountSnackbarId = enqueueSnackbar(
      <Stack direction="row" justifyContent="space-between">
        {message}
        {getCloseButton(() => {
          closeSnackbar(pausedAccountSnackbarId);
        })}
      </Stack>,
      {
        variant: 'info',
        persist: true,
        action: <></>,
      }
    );
    localStorage.setItem(
      'pausedAccountSnackbarId',
      JSON.stringify(pausedAccountSnackbarId)
    );
  }

  function getCloseButton(onClick: () => void) {
    return (
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={onClick}
        sx={{ marginLeft: 1 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    );
  }

  // Removed helper functions moved into hook

  function handleSiteNotifications() {
    if (!account) return;

    const siteNotification = account?.meta?.notification;

    if (
      !siteNotification ||
      seenSiteNotifications.includes(siteNotification.title)
    ) {
      return;
    }

    enqueueSnackbar(
      <Text>
        <b>{siteNotification.title}</b> <br /> {siteNotification.message}
      </Text>,
      {
        variant: 'info',
        persist: true,
      }
    );

    setSeenSiteNotifications((prevState) => [
      ...prevState,
      siteNotification.title,
    ]);
  }

  const isDashboardHome = location.pathname === '/dashboard/home';
  const isRewards = location.pathname.includes('/dashboard/rewards');
  const isApiPage = location.pathname.includes('/dashboard/api');
  const isRecentReports = location.pathname.includes('/dashboard/reports');

  const showRightRail = !isDashboardHome && !isRewards && !isApiPage;

  const RightRailComponent = (
    <Grid
      container
      size={{ xs: 12, md: 3, lg: 3 }}
      spacing={1}
      direction="column"
      marginTop={2}
      maxWidth={{ xs: '100%', md: '299px' }}
    >
      {isMobile && isRecentReports && (
        <Grid width={'100%'} order={1} key="mobile-migration-card">
          <StyledContainer>
            <SideBarMigrationCta />
          </StyledContainer>
        </Grid>
      )}
      {location.pathname.includes('/dashboard/billing') && (
        <Grid
          width={'100%'}
          order={{ xs: 6, md: 1 }}
          className="needs-assistance"
        >
          <NeedAssistanceSidebar
            logout={handleLogout}
            supportImgUrl={constants.images.img24hr}
            contactUsHandler={contactUsHandler}
          />
        </Grid>
      )}
      {location.pathname.includes('/dashboard/monitoring') && (
        <Grid width={'100%'} order={2}>
          <EmailSettingsCard
            currentInstantAlertsSetting={
              account?.account?.alert_me.settings.instant_enabled || false
            }
            currentDigestAlertsSetting={
              account?.account?.alert_me.settings.digest_enabled || false
            }
            handleMonitoredReports={() => setMonitoredReportsOpen(true)}
          />
          <MonitoredReportsModal
            isOpen={monitoredReportsOpen}
            onClose={() => closeMonitoredReportsModal()}
          />
        </Grid>
      )}

      {videoArticles?.articles && (
        <Grid width={'100%'} order={4}>
          <ArticlesContainer>
            <RelatedVideoArticles videoArticles={videoArticles.articles} />
          </ArticlesContainer>
        </Grid>
      )}
      {articles && (
        <Grid width={'100%'} order={5}>
          <ArticlesContainer>
            <RelatedArticles articles={articles} />
          </ArticlesContainer>
        </Grid>
      )}
    </Grid>
  );

  const RightRail = showRightRail ? RightRailComponent : <></>;

  return (
    <SidenavContainer>
      <EmailVerificationErrorModal
        open={showEmailExpiredModal}
        onClose={() => setShowEmailExpiredModal(false)}
      />
      <EmailVerificationSuccessModal
        open={showEmailSuccessModal}
        onClose={() => setShowEmailSuccessModal(false)}
      />
      <Grid container justifyContent={'space-around'}>
        <Grid
          size={{
            xs: 12,
            md: showRightRail ? 8 : 12,
            lg: showRightRail ? 8 : 12,
          }}
          maxWidth={{
            xs: '100%',
            md: showRightRail ? '911px !important' : '100%',
          }}
        >
          <Outlet />
        </Grid>
        {RightRail}
      </Grid>
    </SidenavContainer>
  );
}
